import React, {useEffect, useState} from "react";
import {useTitleFromComponent, useDataWithRoute} from "../../../../utils/hooks";
import {useVsiAppContext} from "../../../../stores/VsiApp";
import {VsiBubble, VsiH4,} from "../../../../styled/App.styled";
import Grid from "../../../../components/Grid";
import VsiLabeledSelect from "../../../../components/VsiLabeledSelect";
import Stockchart from "../../../../components/Stockchart";
import {hostIopsLatency} from "../../../../utils/gridColumnDefs/hosts/hostIopsLatency";
import {useSearchParams} from "react-router-dom";
import {formatStockChartConfig} from "../../../../utils/Helpers";

import {iopsLineColor, latencyLineColor, mbsecLineColor, usedLineColor} from "../../../../utils/chartUtils";
import {Card} from "reactstrap";


const performanceSeriesLines = [
    {name: 'IOPS', field: 'iosec', value: 'iops', dashStyle: 'solid', dataLabels: {format: '{point.y:,.0f}'}, tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},},
    {name: 'Average Latency', field: 'average_latency', value: 'latency'},
    {name: 'Read Latency', field: 'average_read_latency', value: 'latency', dashStyle: 'DashDot'},
    {name: 'Mb/Sec', field: 'mbsec', value: 'mbsec'},
    {name: 'Used', field: 'used', value: 'used', dataLabels: {format: '{point.y:,.0f}'}, tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},},
];

const performanceTypes = [
    {value: 'iops', label: 'IOPS', color: iopsLineColor},
    {value: 'latency', label: 'Latency (ms)', color: latencyLineColor,},
    {value: 'mbsec', label: 'Mb/Sec', color: mbsecLineColor},
    {value: 'used', label: 'Used (GiB)', color: usedLineColor},
];

export default function HostTrends({context, title}) {

    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const [chartData, setChartData] = useState([]);
    const [formattedChartData, setFormattedChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({});

    const axis1 = searchParams.get('axis_1') || performanceTypes[0].value;
    const axis2 = searchParams.get('axis_2') || performanceTypes[1].value;
    let defaultAxis1 = performanceTypes.find(({value}) => axis1 === value) || performanceTypes[0];
    let defaultAxis2 = performanceTypes.find(({value}) => axis2 === value) || performanceTypes[1];

    const [axis1Type, setAxis1Type] = useState(defaultAxis1);
    const [axis2Type, setAxis2Type] = useState(defaultAxis2);

    const encodedDevice = encodeURIComponent(searchParams.get('device_name'));
    const encodedHost = encodeURIComponent(searchParams.get('host_name'));
    const decodedHost = decodeURIComponent(encodedHost);

    let route = `/api/v6/report/host_iops_latency_proc?client_id=${client.clientId}&host_name=${encodedHost}&device_name=${encodedDevice}`;

    const [data, {isLoading}] = useDataWithRoute(route);

    useEffect(() => {
        setChartData(data?.recordsets?.[0]);
        formatPerformance(data?.recordsets?.[0] || []);
    }, [data, searchParams]);

    const formatPerformance = data => {

        const yAxis1 = [];
        const yAxis2 = [];
        const axis1Color = [];
        const axis2Color = [];

        performanceSeriesLines.forEach((line) => {
            if (line.value === axis1Type.value) {
                yAxis1.push({
                    name: line.name,
                    field: line.field,
                    dashStyle: line.dashStyle,
                    dataLabels: line.dataLabels,
                    tooltip: line.tooltip,
                });
                axis1Color.push(axis1Type.color);
            } else if (line.value === axis2Type.value) {
                yAxis2.push({
                    name: line.name,
                    field: line.field,
                    dashStyle: line.dashStyle,
                    dataLabels: line.dataLabels,
                    tooltip: line.tooltip,
                    yAxis: 1,
                });
                axis2Color.push(axis2Type.color);
            }
        });

        let config = formatStockChartConfig(data, [...yAxis1, ...yAxis2]);

        setChartConfig({
            yAxis: [
                {
                    title: {
                        text: axis1Type.label,
                    }
                },
                {
                    title: {
                        text: axis2Type.label,
                    },
                    opposite: true
                }
            ],
            colors: [...axis1Color, ...axis2Color]
        });
        setFormattedChartData(config.series);
    };

    const onAxis1Change = (axis) => {
        setAxis1Type(axis);
        searchParams.set('axis_1', axis.value);
        setSearchParams(searchParams, {replace: true});
    };


    const onAxis2Change = (axis) => {
        setAxis2Type(axis);
        searchParams.set('axis_2', axis.value);
        setSearchParams(searchParams, {replace: true});
    };

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if(node.data) filteredData.push(node.data);
        });

        formatPerformance(filteredData);
    };

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Host Trends - {decodedHost}</VsiH4>
        <VsiBubble noAutobot>
            <div className={"w-100 d-flex mb-2"}>
                <VsiLabeledSelect options={performanceTypes.filter(({value}) => value !== axis2Type.value)}
                                  isLoading={isLoading}
                                  isDisabled={isLoading}
                                  label={'Left Axis'}
                                  minWidth={200}
                                  maxWidth={225}
                                  onChange={onAxis1Change}
                                  defaultValue={axis1Type}/>
                <VsiLabeledSelect options={performanceTypes.filter(({value}) => value !== axis1Type.value)}
                                  isLoading={isLoading}
                                  isDisabled={isLoading}
                                  label={'Right Axis'}
                                  minWidth={200}
                                  maxWidth={225}
                                  onChange={onAxis2Change}
                                  defaultValue={axis2Type}/>
            </div>
        </VsiBubble>
        <VsiBubble autobotInfo={{ secondaryMetric: [axis1Type, axis2Type] }}>
            <Stockchart series={formattedChartData}
                        saveParams={{
                            hostName: encodedHost,
                            deviceName: encodedDevice,
                        }}
                        config={chartConfig}/>
        </VsiBubble>
        <VsiBubble>
            <Grid data={chartData || []} columnDefs={hostIopsLatency} filterChanged={filterChanged}/>
        </VsiBubble>
    </Card>
}
